// ワールドマップ（国盗り）の描画部品：島の絵地図＋国の領域の重ね描き（SVG）＋国の札、国の拡大図（章の絵地図に5地点）、小さな島の地図。
// 国の領域は tools/art/worldmap-regions.ts が島の絵から作った輪郭（data/worldmap-regions.ts）。国境・塗り・霧は画像に描かず、ここで重ねる。
import { COUNTRIES, COUNTRY_BY_ID, provinceLabel, PROVINCES, PROVINCE_BY_ID, STAGES, provinceStages, type ProvinceDef, type ProvinceView } from '@hyakki/core';
import { COUNTRY_PATHS, REGION_LABEL, REGION_PATHS, WORLD_H, WORLD_W } from '../data/worldmap-regions';
import { mapSrc } from '../assets';
import { esc, starsHtml } from './dom';
import { icon } from './icons';

export const ISLAND_SRC = '/art/worldmap/island.webp';

/** その妖が出る国（式神の候補の「どこで会えるか」）。章ボスは大将の国、エリートは同じ難易度の2国 */
export function provincesOfEnemy(enemyId: string): ProvinceDef[] {
  return PROVINCES.filter((p) => provinceStages(p).some((s) => STAGES[s - 1]?.enemyIds.includes(enemyId) || STAGES[s - 1]?.bossId === enemyId));
}

/** 章の絵地図の上の10地点（%。左下→右上の道に沿う）。国の5戦は章内の番号の地点に置く */
const MAP_POINTS: [number, number][][] = [
  [[14, 85], [27, 75], [41, 65], [50, 56], [59, 48], [58, 39], [67, 31], [77, 22], [83, 13], [89, 6]],
  [[12, 86], [22, 74], [33, 64], [42, 55], [50, 47], [60, 40], [70, 32], [77, 24], [85, 15], [89, 6]],
  [[16, 88], [24, 78], [30, 69], [42, 61], [52, 54], [57, 46], [62, 38], [69, 29], [73, 20], [76, 9]],
  [[14, 87], [22, 79], [30, 72], [42, 64], [52, 56], [58, 47], [64, 39], [72, 30], [78, 21], [83, 10]],
  [[10, 88], [18, 80], [27, 71], [35, 61], [47, 53], [62, 46], [70, 36], [73, 26], [78, 16], [88, 7]],
  [[10, 88], [20, 80], [31, 72], [38, 62], [44, 54], [55, 46], [66, 39], [74, 30], [79, 20], [83, 10]],
  [[15, 88], [17, 78], [20, 68], [28, 58], [40, 52], [52, 46], [60, 37], [70, 29], [80, 20], [90, 8]],
  [[18, 86], [26, 76], [33, 68], [41, 59], [52, 50], [60, 41], [63, 33], [70, 25], [80, 16], [88, 8]],
  [[14, 85], [23, 76], [33, 69], [42, 60], [50, 52], [57, 45], [65, 37], [71, 29], [77, 19], [84, 8]],
  [[12, 86], [17, 76], [24, 68], [35, 59], [46, 51], [54, 43], [60, 36], [68, 28], [75, 19], [83, 10]],
];

/** 国の拡大図の上の地点（%）。大将の国は絵を左右反転して使う（同じ難易度の2国で見た目を変える） */
export function fieldPoint(p: ProvinceDef, stageId: number): [number, number] {
  const idx = (stageId - 1) % 10;
  const [x, y] = (MAP_POINTS[p.difficulty - 1] ?? MAP_POINTS[0])[idx] ?? [50, 50];
  return p.boss ? [100 - x, y] : [x, y];
}

const pct = (v: number, of: number) => `${((v / of) * 100).toFixed(2)}%`;
/** 札の位置の手直し（絵の px）。北の端の2国は国境から遠い点どうしが近く、札が重なるので離す */
const LABEL_NUDGE: Record<string, [number, number]> = { p20: [-75, 20], p19: [12, -30], p01: [0, 45], p07: [30, 15], p09: [-22, 0], p10: [18, 12] };
const COUNTRY_COLORS = ['#d89b58','#69b9dd','#a9bf66','#d9b285','#b0d8ed','#eb9071','#ba9acc','#78bca1','#d798a2','#c4b172'];
const labelAt = (id: string): [number, number] => {
  const [x, y] = REGION_LABEL[id] ?? [0, 0];
  const [dx, dy] = LABEL_NUDGE[id] ?? [0, 0];
  return [x + dx, y + dy];
};

export interface IslandOpts {
  views: Record<string, ProvinceView>;
  selected: string | null;
  /** 制圧した国の数（未解放の国の札に出す） */
  conquered: number;
}

/** 島の絵地図（.wm-sea の中で縦横比を保って最大に。大きさは fitIsland が合わせる） */
export function islandHtml(o: IslandOpts): string {
  const by = (st: string) => PROVINCES.filter((p) => o.views[p.id]?.state === st);
  const paths = (list: readonly ProvinceDef[], cls: string, extra = '') => list.map((p) => `<path class="${cls}" d="${REGION_PATHS[p.id]}" ${extra}/>`).join('');
  const fogged = [...by('fog'), ...by('gated'), ...by('frontier')];
  const sel = o.selected ? PROVINCE_BY_ID[o.selected] : null;
  const tags = PROVINCES.map((p) => {
    const v = o.views[p.id];
    const st = v?.state ?? 'fog';
    const [lx, ly] = labelAt(p.id);
    return `<button class="wm-tag ${st} ${p.id === o.selected ? 'on' : ''}" style="left:${pct(lx, WORLD_W)};top:${pct(ly, WORLD_H)}" data-act="prov" data-id="${p.id}"
        aria-label="${esc(provinceLabel(p))}（${STATE_LABEL[st]}）">
      ${st === 'conquered' ? '<i class="wm-flag" aria-hidden="true"></i>' : ''}
      <span class="wm-plate" style="--country-color:${COUNTRY_COLORS[p.difficulty - 1]}"><small>${esc(COUNTRY_BY_ID[p.countryId].name)}</small><b>${esc(p.short)}</b></span>
      ${st === 'open' ? `<i class="wm-atk" aria-hidden="true">${v && v.cleared > 0 ? v.cleared : icon('sortie')}</i>` : ''}
      ${st === 'frontier' || st === 'gated' || (st === 'fog' && p.gate > 0) ? `<i class="wm-gate" aria-hidden="true">${icon('lock')}</i>` : ''}
    </button>`;
  }).join('');
  return `<div class="wm-island" data-fit-w="${WORLD_W}" data-fit-h="${WORLD_H}">
    <img class="wm-img" src="${ISLAND_SRC}" alt="" draggable="false">
    <svg class="wm-svg" viewBox="0 0 ${WORLD_W} ${WORLD_H}" aria-hidden="true">
      <defs>
        <filter id="wmFog" x="-5%" y="-5%" width="110%" height="110%">
          <feTurbulence type="fractalNoise" baseFrequency="0.011" numOctaves="4" seed="7" result="n"/>
          <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.93  0 0 0 0 0.94  0 0 0 0 0.96  0 0 0 2.8 -0.78" result="cloud"/>
          <feGaussianBlur in="SourceGraphic" stdDeviation="7" result="soft"/>
          <feComposite in="cloud" in2="soft" operator="in" result="c"/>
          <feMerge><feMergeNode in="soft"/><feMergeNode in="c"/></feMerge>
        </filter>
        <filter id="wmGlow" x="-10%" y="-10%" width="120%" height="120%"><feGaussianBlur stdDeviation="6" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      </defs>
      <g class="wm-l-country-fill">${COUNTRIES.map((c,i) => `<path d="${COUNTRY_PATHS[c.id]}" fill="${COUNTRY_COLORS[i]}"/>`).join('')}</g>
      <g class="wm-l-con">${paths(by('conquered'), 'con')}</g>
      <g class="wm-l-fog" filter="url(#wmFog)">${paths(fogged, 'fog')}</g>
      <g class="wm-l-border">${paths(PROVINCES, 'bd')}</g>
      <g class="wm-l-country-border">${COUNTRIES.map(c => `<path d="${COUNTRY_PATHS[c.id]}"/>`).join('')}</g>
      <g class="wm-l-open" filter="url(#wmGlow)">${paths(by('open'), 'open')}</g>
      ${sel ? `<path class="wm-sel" d="${REGION_PATHS[sel.id]}"/>` : ''}
      <g class="wm-l-hit">${PROVINCES.map((p) => `<path d="${REGION_PATHS[p.id]}" data-act="prov" data-id="${p.id}"/>`).join('')}</g>
    </svg>
    <div class="wm-tags">${tags}</div>
  </div>`;
}

export const STATE_LABEL: Record<string, string> = { conquered: '制圧済み', open: '攻め込める', gated: '未解放', fog: '未踏', frontier: '前線攻略待ち' };

/** [data-fit-w][data-fit-h] の要素（島の絵・国の拡大図）を、親の中で縦横比どおりに最大化する。戻り値で解除 */
export function fitMaps(root: HTMLElement): () => void {
  const fit = () => {
    for (const box of root.querySelectorAll<HTMLElement>('[data-fit-w]')) {
      const parent = box.parentElement;
      if (!parent) continue;
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      if (!w || !h) continue;
      const fw = Number(box.dataset.fitW);
      const fh = Number(box.dataset.fitH);
      const k = Math.min(w / fw, h / fh);
      box.style.width = `${Math.floor(fw * k)}px`;
      box.style.height = `${Math.floor(fh * k)}px`;
    }
  };
  const ro = new ResizeObserver(fit);
  ro.observe(root);
  fit();
  return () => ro.disconnect();
}

/** 小さな島の地図（国の拡大図の隅。押すと島へ戻る） */
export function miniIslandSvg(selected: string, views: Record<string, ProvinceView>): string {
  return `<svg class="wm-mini" viewBox="0 0 ${WORLD_W} ${WORLD_H}" aria-hidden="true">${PROVINCES.map(
    (p) => `<path d="${REGION_PATHS[p.id]}" class="${p.id === selected ? 'on' : (views[p.id]?.state ?? 'fog')}"/>`,
  ).join('')}</svg>`;
}

export interface ProvinceMapField {
  stageId: number;
  state: 'cleared' | 'open' | 'locked';
  stars: readonly boolean[];
}

/** 国の拡大図：その難易度の章の絵地図に、5戦の地点 */
export function provinceMapHtml(p: ProvinceDef, fields: ProvinceMapField[], selected: number, mini: string): string {
  const nodes = provinceStages(p)
    .map((s, i) => {
      const f = fields.find((x) => x.stageId === s);
      const [x, y] = fieldPoint(p, s);
      const castle = i === 4;
      // 防衛戦：城攻めと同じ大きな印（盾）で目立たせる
      const defense = STAGES[s - 1]?.mode === 'defense';
      const locked = f?.state === 'locked';
      return `<button class="node pv-node ${s === selected ? 'on' : ''} ${castle ? 'boss castle' : defense ? 'boss defense' : ''} ${locked ? 'locked' : ''} ${f?.state === 'cleared' ? 'cleared' : ''}" style="left:${x}%;top:${y}%"
        data-act="field" data-id="${s}" aria-label="${esc(p.fields[i])}${defense ? '（防衛）' : ''}${locked ? '（まだ攻められない）' : ''}">
        <span class="node-mark">${icon(locked ? 'lock' : castle ? 'castle' : defense ? 'shield' : 'sortie')}</span>
        <span class="node-label">${esc(p.fields[i])}</span>
        ${f && f.state === 'cleared' ? `<span class="node-stars">${starsHtml(f.stars, false)}</span>` : ''}
      </button>`;
    })
    .join('');
  // 絵（縦3:4）は縦横比を保って最大に（地点が道に乗るように）。余白は同じ絵をぼかして埋める
  return `<div class="pv-map">
    <div class="pv-blur ${p.boss ? 'mirror' : ''}" style="background-image:url(${mapSrc(p.difficulty)})"></div>
    <div class="pv-fitbox"><div class="pv-frame" data-fit-w="768" data-fit-h="1024">
      <div class="pv-bg ${p.boss ? 'mirror' : ''}" style="background-image:url(${mapSrc(p.difficulty)})"></div>
      ${nodes}
    </div></div>
    <div class="pv-corner">
      <span class="pv-vname" aria-hidden="true">${esc(provinceLabel(p))}</span>
      <button class="pv-back" data-act="island" aria-label="島の地図へ戻る">${mini}<span>${icon('left')}島へ</span></button>
    </div>
  </div>`;
}
